import {
  Card,
  Checkbox,
  CheckboxCheckedChangeDetails,
  createListCollection,
  Field,
  Flex,
  Heading,
  Input,
  Link,
  Portal,
  RadioGroup,
  Select,
  SelectValueChangeDetails,
  Stack,
  Text,
  VStack,
} from '@chakra-ui/react';
import { memo, useCallback, useState } from 'react';
import { FaPercent } from 'react-icons/fa';

import CalendarPickerPill from '@components/common/CalendarPickerPill';
import TimePickerPill from '@components/common/TimePickerPill';
import { InputGroup } from '@components/ui/input-group';
import { SearchIcon } from 'vectors';

const eligibilityItems = [
  {
    value: 'all',
    label: 'All customers',
  },
  {
    value: 'specificCustomers',
    label: 'Specific customers',
  },
];

const discountTypes = createListCollection({
  items: [
    {
      value: 'percentage',
      label: 'Percentage',
    },
    {
      value: 'fixed',
      label: 'Fixed',
    },
  ],
});

const applicableToItems = createListCollection({
  items: [
    {
      value: 'all',
      label: 'All locations',
    },
    {
      value: 'specificLocations',
      label: 'Specific locations',
    },
  ],
});

const CreateGiftCardsForm = () => {
  const [isEndDateSet, setIsEndDateSet] = useState(false);
  const [discountType, setDiscountType] = useState<string[]>(['percentage']);
  const [applicableTo, setApplicableTo] = useState<string[]>(['all']);

  const handleDiscountTypeChange = useCallback(
    (details: SelectValueChangeDetails<{ value: string; label: string }>) => {
      setDiscountType(details.value);
    },
    [],
  );

  const handleApplicableToChange = useCallback(
    (details: SelectValueChangeDetails<{ value: string; label: string }>) => {
      setApplicableTo(details.value);
    },
    [],
  );

  const onCheckedChange = useCallback((details: CheckboxCheckedChangeDetails) => {
    if (details.checked !== 'indeterminate') {
      setIsEndDateSet(details.checked);
    }
  }, []);

  return (
    <Flex flexDir="column" gap={4} w="full">
      <Card.Root bg="surface.background" borderColor="border.light">
        <Card.Header py={1}>
          <Heading fontSize="sm">Discount code</Heading>
        </Card.Header>
        <Card.Body py={4}>
          <Field.Root required>
            <Field.Label gap={2} fontSize="sm">
              Code <Link color="blue.500">Generate random code</Link>
            </Field.Label>
            <Input borderColor="border.light" size="xs" maxW="md" />
            <Field.HelperText>Customers must enter this code at checkout.</Field.HelperText>
          </Field.Root>
        </Card.Body>
      </Card.Root>
      <Card.Root bg="surface.background" borderColor="border.light">
        <Card.Header py={1}>
          <Heading fontSize="sm">Discount value</Heading>
        </Card.Header>
        <Card.Body gap={4}>
          <Stack direction={{ base: 'column', md: 'row' }}>
            <Select.Root
              collection={discountTypes}
              size="sm"
              borderColor="border.light"
              maxW="md"
              defaultValue={['percentage']}
              onValueChange={handleDiscountTypeChange}
            >
              <Select.HiddenSelect />
              <Select.Control>
                <Select.Trigger borderColor="border.light">
                  <Select.ValueText />
                </Select.Trigger>
                <Select.IndicatorGroup>
                  <Select.Indicator />
                </Select.IndicatorGroup>
              </Select.Control>
              <Portal>
                <Select.Positioner maxW="md">
                  <Select.Content bg="surface.background">
                    {discountTypes.items.map((type) => (
                      <Select.Item item={type} key={type.value}>
                        {type.label}
                        <Select.ItemIndicator />
                      </Select.Item>
                    ))}
                  </Select.Content>
                </Select.Positioner>
              </Portal>
            </Select.Root>
            <InputGroup
              endElement={discountType.includes('percentage') ? <FaPercent /> : undefined}
              startElement={
                discountType.includes('fixed') ? <Text fontSize="2xs">GHS</Text> : undefined
              }
              maxW="sm"
            >
              <Input borderColor="border.light" size="sm" />
            </InputGroup>
          </Stack>
          <Stack>
            <Select.Root
              collection={applicableToItems}
              size="sm"
              borderColor="border.light"
              maxW="md"
              defaultValue={['all']}
              onValueChange={handleApplicableToChange}
            >
              <Select.HiddenSelect />
              <Select.Label>Applies to</Select.Label>
              <Select.Control>
                <Select.Trigger borderColor="border.light">
                  <Select.ValueText />
                </Select.Trigger>
                <Select.IndicatorGroup>
                  <Select.Indicator />
                </Select.IndicatorGroup>
              </Select.Control>
              <Portal>
                <Select.Positioner maxW="md">
                  <Select.Content bg="surface.background">
                    {applicableToItems.items.map((item) => (
                      <Select.Item item={item} key={item.value}>
                        {item.label}
                        <Select.ItemIndicator />
                      </Select.Item>
                    ))}
                  </Select.Content>
                </Select.Positioner>
              </Portal>
            </Select.Root>
            {applicableTo.includes('specificLocations') && (
              <InputGroup startElement={<SearchIcon size="xs" />}>
                <Input
                  borderColor="border.light"
                  size="sm"
                  maxW="md"
                  placeholder="Search locations"
                />
              </InputGroup>
            )}
          </Stack>
        </Card.Body>
      </Card.Root>
      <Card.Root bg="surface.background" borderColor="border.light">
        <Card.Header py={1}>
          <Heading fontSize="sm">Eligibility</Heading>
        </Card.Header>
        <Card.Body py={4}>
          <RadioGroup.Root defaultValue="all" orientation="vertical">
            <VStack align="start">
              {eligibilityItems.map((item) => (
                <RadioGroup.Item key={item.value} value={item.value} cursor="pointer">
                  <RadioGroup.ItemHiddenInput />
                  <RadioGroup.ItemIndicator borderColor="border.light" />
                  <RadioGroup.ItemText>{item.label}</RadioGroup.ItemText>
                </RadioGroup.Item>
              ))}
            </VStack>
          </RadioGroup.Root>
        </Card.Body>
      </Card.Root>
      <Card.Root bg="surface.background" borderColor="border.light">
        <Card.Header py={1}>
          <Heading fontSize="sm">Maximum discount uses</Heading>
        </Card.Header>
        <Card.Body py={4}>
          <VStack align="start">
            <Checkbox.Root variant="solid" cursor="pointer">
              <Checkbox.HiddenInput />
              <Checkbox.Control borderColor="border.light">
                <Checkbox.Indicator />
              </Checkbox.Control>
              <Checkbox.Label>
                Limit number of times this discount can be used in total
              </Checkbox.Label>
            </Checkbox.Root>
            <Checkbox.Root variant="solid" cursor="pointer">
              <Checkbox.HiddenInput />
              <Checkbox.Control borderColor="border.light">
                <Checkbox.Indicator />
              </Checkbox.Control>
              <Checkbox.Label>Limit to one user per customer</Checkbox.Label>
            </Checkbox.Root>
          </VStack>
        </Card.Body>
      </Card.Root>
      <Card.Root bg="surface.background" borderColor="border.light">
        <Card.Header py={1}>
          <Heading fontSize="sm">Active dates</Heading>
        </Card.Header>
        <Card.Body py={4}>
          <Stack gap={4}>
            <Flex flexDir="row" gap={8}>
              <CalendarPickerPill title="Start date" />
              <TimePickerPill title="Start time" />
            </Flex>
            <Checkbox.Root variant="solid" cursor="pointer" onCheckedChange={onCheckedChange}>
              <Checkbox.HiddenInput />
              <Checkbox.Control borderColor="border.light">
                <Checkbox.Indicator />
              </Checkbox.Control>
              <Checkbox.Label>Set end date</Checkbox.Label>
            </Checkbox.Root>
            {isEndDateSet && (
              <Flex flexDir="row" gap={8}>
                <CalendarPickerPill title="End date" />
                <TimePickerPill title="End time" />
              </Flex>
            )}
          </Stack>
        </Card.Body>
      </Card.Root>
    </Flex>
  );
};

export default memo(CreateGiftCardsForm);
